import type { ReactNode } from 'react';
import { Button, Logo } from '@auctisync/ui';

/**
 * Shared Layout for every auctisync.com marketing page.
 *
 * Renders a consistent nav + footer so each page component only needs to
 * worry about its own main-column content. SEO metadata is set per-page
 * by {@link PageHead}.
 */
export function Layout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col">
      <Nav />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function Nav() {
  return (
    <header className="border-b" style={{ borderColor: 'var(--border)' }}>
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <a href="/" className="flex items-center" style={{ color: 'var(--text)' }}>
          <Logo height={26} />
        </a>
        <nav
          className="hidden items-center gap-8 text-sm md:flex"
          style={{ color: 'var(--text-muted)' }}
        >
          <a href="/for-auction-houses" className="hover:text-[color:var(--text)]">
            Auction Houses
          </a>
          <a href="/for-facilities" className="hover:text-[color:var(--text)]">
            Facilities
          </a>
          <a href="/for-liquidators" className="hover:text-[color:var(--text)]">
            Liquidators
          </a>
          <a href="/for-specialty" className="hover:text-[color:var(--text)]">
            Specialty
          </a>
          <a href="/pricing" className="hover:text-[color:var(--text)]">
            Pricing
          </a>
        </nav>
        <Button asChild size="sm">
          <a href="/contact">Request Demo</a>
        </Button>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t py-10" style={{ borderColor: 'var(--border)' }}>
      <div
        className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-6 md:flex-row md:items-center"
        style={{ color: 'var(--text-muted)' }}
      >
        <div className="flex flex-col gap-1">
          <span className="font-bold" style={{ color: 'var(--text)' }}>
            AuctiSync
          </span>
          <span className="text-sm">The auction platform built for what&apos;s next.</span>
        </div>
        <div className="flex flex-wrap gap-6 text-sm">
          <a href="/pricing" className="hover:text-[color:var(--text)]">
            Pricing
          </a>
          <a href="/case-studies/dta" className="hover:text-[color:var(--text)]">
            Case Studies
          </a>
          <a href="https://help.auctisync.com" className="hover:text-[color:var(--text)]">
            Docs
          </a>
          <a href="/about" className="hover:text-[color:var(--text)]">
            About
          </a>
          <a href="/contact" className="hover:text-[color:var(--text)]">
            Contact
          </a>
        </div>
      </div>
    </footer>
  );
}
